import { Component, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../../services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { CcoTestOrderRequest, CcoTestOrderResource } from "./admin-view-cco-test.model";
import { Team } from "../../generated-models/Team";

interface ResourceTypePreset {
  label: string;
  service: string;
  unit: string;
  requiresSku: boolean;
}

interface CcoTestResourceRow extends CcoTestOrderResource {
  presetLabel: string;
  requiresSku: boolean;
}

interface TeamOption {
  Oid: string;
  Label: string;
}

interface RegionOption {
  RegionName: string;
  Label: string;
}

@Component({
  standalone: false,
  selector: "app-admin-view-cco-test",
  templateUrl: "./admin-view-cco-test.component.html",
  styleUrls: ["../../styles.scss"],
})
export class AdminViewCcoTestComponent implements OnInit {
  // Resource type presets covering every resource the Capacity Order service supports.
  readonly resourceTypePresets: ResourceTypePreset[] = [
    { label: "VM cores", service: "VM", unit: "cores", requiresSku: true },
    { label: "Storage accounts", service: "STORAGE", unit: "accounts", requiresSku: false },
    { label: "Batch subscription quota", service: "BATCH", unit: "accounts", requiresSku: false },
    { label: "Batch pool quota", service: "BATCH", unit: "pools", requiresSku: false },
    { label: "Batch job quota", service: "BATCH", unit: "jobs", requiresSku: false },
    { label: "Batch low-priority cores", service: "BATCH", unit: "cores", requiresSku: true },
    { label: "CosmosDB accounts", service: "COSMOSDB", unit: "accounts", requiresSku: false },
    { label: "SQL vCores", service: "SQL", unit: "vcores", requiresSku: false },
    { label: "SQL MI subnets", service: "SQLMI", unit: "subnets", requiresSku: false },
    { label: "SQL MI vCores", service: "SQLMI", unit: "vcores", requiresSku: false },
  ];

  serviceTreeId = "";
  region = "";
  serviceTeam = "";
  teamContact = "";
  isHobo = false;
  needByDate = "";
  blueprint = "CcoTest";

  resources: CcoTestResourceRow[] = [];

  teams: Team[] = [];

  teamOptions: TeamOption[] = [];

  regionOptions: RegionOption[] = [];

  vmSkuOptions: string[] = [];

  lookupOrderId = "";
  responseText = "";

  constructor(
    private apiService: ApiService,
    private notificationService: ToastrService,
    private loadingService: LoadingScreenService
  ) {}

  ngOnInit(): void {
    this.addResource();
    this.loadTeams();
    this.loadRegions();
    this.loadCurrentUser();
  }

  loadTeams(): void {
    this.apiService.getTeamList().subscribe(
      (teams) => {
        this.teams = (teams || []).sort((a, b) => (a.Name || "").localeCompare(b.Name || ""));
        this.teamOptions = this.teams.map((t) => ({
          Oid: t.Oid,
          Label: `${t.Name} (${t.Oid})`,
        }));
        this.onServiceTreeIdChange();
      },
      (e: any) => {
        this.notificationService.error(e);
      }
    );
  }

  loadRegions(): void {
    this.apiService.getRegionList().subscribe(
      (regions) => {
        this.regionOptions = (regions || [])
          .filter((r) => r.RegionName)
          .sort((a, b) => (a.RegionName || "").localeCompare(b.RegionName || ""))
          .map((r) => ({
            RegionName: r.RegionName,
            Label: r.ArmRegionName && r.ArmRegionName !== r.RegionName ? `${r.RegionName} (${r.ArmRegionName})` : r.RegionName,
          }));
      },
      (e: any) => {
        this.notificationService.error(e);
      }
    );
  }

  loadCurrentUser(): void {
    this.apiService.getUserProfile().then((profile) => {
      if (profile?.Email) {
        this.teamContact = profile.Email;
      }
    });
  }

  onServiceTreeIdChange(): void {
    const team = this.teams.find((t) => t.Oid === this.serviceTreeId);
    this.serviceTeam = team ? team.Name : "";
  }

  onRegionChange(): void {
    this.vmSkuOptions = [];
    this.resources.forEach((r) => {
      if (r.requiresSku) {
        r.Sku = "";
      }
    });

    if (this.region) {
      this.loadVmSkus();
    }
  }

  addResource(): void {
    const defaultPreset = this.resourceTypePresets[0];
    this.resources.push({
      presetLabel: defaultPreset.label,
      requiresSku: defaultPreset.requiresSku,
      Service: defaultPreset.service,
      Unit: defaultPreset.unit,
      Sku: "",
      Quota: 0,
      SubscriptionCount: 1,
    });
  }

  removeResource(index: number): void {
    this.resources.splice(index, 1);
  }

  onPresetChange(row: CcoTestResourceRow): void {
    const preset = this.resourceTypePresets.find((p) => p.label === row.presetLabel);
    if (preset) {
      row.Service = preset.service;
      row.Unit = preset.unit;
      row.requiresSku = preset.requiresSku;
      if (!preset.requiresSku) {
        row.Sku = "";
      }
    }
  }

  loadVmSkus(): void {
    if (!this.region) {
      this.notificationService.warning("Enter a region before loading VM SKUs.");
      return;
    }

    this.loadingService.setLoading(true);
    this.apiService.getVmSkusByRegion(this.region).subscribe(
      (skus) => {
        this.vmSkuOptions = Array.from(new Set((skus || []).map((s) => s.CrpVmSku).filter((s) => !!s))).sort();
        this.loadingService.setLoading(false);
        this.notificationService.info(`Loaded ${this.vmSkuOptions.length} VM SKUs for ${this.region}.`);
      },
      (e: any) => {
        this.vmSkuOptions = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  createOrder(): void {
    if (!this.serviceTreeId || !this.region) {
      this.notificationService.warning("Service Tree Id and Region are required.");
      return;
    }

    if (this.resources.length === 0) {
      this.notificationService.warning("Add at least one resource.");
      return;
    }

    const request: CcoTestOrderRequest = {
      ServiceTreeId: this.serviceTreeId.trim(),
      Region: this.region.trim(),
      ServiceTeam: this.serviceTeam.trim(),
      TeamContact: this.teamContact.trim(),
      IsHobo: this.isHobo,
      NeedByDate: this.needByDate ? new Date(this.needByDate).toISOString() : undefined,
      Blueprint: this.blueprint.trim(),
      Resources: this.resources.map((r) => ({
        Service: r.Service,
        Unit: r.Unit,
        Sku: r.Sku,
        Quota: Number(r.Quota),
        SubscriptionCount: Number(r.SubscriptionCount),
      })),
    };

    this.loadingService.setLoading(true);
    this.apiService.createCcoTestOrder(request).subscribe(
      (result) => {
        this.responseText = JSON.stringify(result, null, 2);
        this.loadingService.setLoading(false);
        if (result?.OrderId) {
          this.lookupOrderId = result.OrderId;
          this.notificationService.success(`Capacity order created: ${result.OrderId} (${result.Status || "unknown"}).`);
        } else {
          this.notificationService.warning(`Capacity order response returned no order id. Status: ${result?.Status || "unknown"}.`);
        }
      },
      (e: any) => {
        this.responseText = typeof e === "string" ? e : JSON.stringify(e, null, 2);
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  getOrder(): void {
    if (!this.lookupOrderId) {
      this.notificationService.warning("Enter an order id.");
      return;
    }

    this.loadingService.setLoading(true);
    this.apiService.getCcoTestOrder(this.lookupOrderId.trim()).subscribe(
      (order) => {
        this.responseText = JSON.stringify(order, null, 2);
        this.loadingService.setLoading(false);
      },
      (e: any) => {
        this.responseText = typeof e === "string" ? e : JSON.stringify(e, null, 2);
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  deleteOrder(): void {
    if (!this.lookupOrderId) {
      this.notificationService.warning("Enter an order id.");
      return;
    }

    this.loadingService.setLoading(true);
    this.apiService.deleteCcoTestOrder(this.lookupOrderId.trim()).subscribe(
      () => {
        this.responseText = `Deleted capacity order ${this.lookupOrderId}.`;
        this.loadingService.setLoading(false);
        this.notificationService.success(`Deleted capacity order ${this.lookupOrderId}.`);
      },
      (e: any) => {
        this.responseText = typeof e === "string" ? e : JSON.stringify(e, null, 2);
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }
}
